import { devtools } from 'zustand/middleware';

import { type BaseCurrencyType } from '@pkg/types';
import { roundNumber } from '@pkg/utils';

import { create } from '@app/store/zustand/helpers/create';

import { type ExchangeRates } from './types';
import { INITIAL_RATES, PRETTY_DECIMALS } from './utilities';

export interface ExchangeRatesState {
    rates: ExchangeRates;
    setRate: (currency: BaseCurrencyType, value: number) => void;
    setRates: (newRates: ExchangeRates) => void;
    convertCurrency: (from: BaseCurrencyType, to: BaseCurrencyType, value?: number, round?: boolean) => number;
}

export const STORE_KEY_EXCHANGE_RATES = 'exchange-rates-store';

export const useExchangeRatesStore = create<ExchangeRatesState>()(
    devtools(
        (set, get) => ({
            rates: INITIAL_RATES,
            setRate: (currency, value) =>
                set((state) => ({
                    rates: {
                        ...state.rates,
                        [currency]: value,
                    },
                })),
            setRates: (newRates) => set(() => ({ rates: newRates })),
            convertCurrency: (from, to, value = 0, round = false) => {
                const fromRate = get().rates[from];
                const toRate = get().rates[to];

                const fromValue = value * fromRate;
                const toValue = fromValue / toRate;

                // Keeping "non-usd" values to a max of 8 decimals.
                // Otherwise, simply don't request the "rounded" result.
                const decimals = to === 'usd' ? PRETTY_DECIMALS.usd : PRETTY_DECIMALS.crypto.ideal;
                const converted = isNaN(toValue) ? 0 : toValue;

                return round ? roundNumber(converted, decimals) : converted;
            },
        }),
        {
            name: STORE_KEY_EXCHANGE_RATES,
        },
    ),
);

export function getConversion(from, to, value = 0, round = false) {
    return useExchangeRatesStore.getState().convertCurrency(from, to, value, round);
}
